/**
 * RentYieldGuardrail — market-class A/B/C selector with a red/green light
 * that tells the operator whether their target rent hits the yield floor
 * for that market tier. Renders inline on the Rental tab above the
 * inputs so decisions are pre-anchored.
 *
 * Market yield floors (industry norm):
 *   A markets · top of major metros              → 1.0 – 1.5 % rent-to-price
 *   B markets · outside major metros / suburbs   → 1.5 – 2.25 %
 *   C markets · rural / high-crime               → 2.5 – 3 %
 *
 * Red light = falls below the FLOOR of the chosen class.
 * Amber    = inside the target band but below the top.
 * Green    = at or above the top of the band.
 */
import React, { useMemo, useState } from "react";
import { AlertTriangle, Check, TrendingUp } from "lucide-react";

const CLASSES = [
  { key: "A", label: "A · Major metro core",       min: 0.010, max: 0.015 },
  { key: "B", label: "B · Suburban / secondary",   min: 0.015, max: 0.0225 },
  { key: "C", label: "C · Rural / tertiary",       min: 0.025, max: 0.030 },
];

export default function RentYieldGuardrail({
  purchasePrice, monthlyRent, defaultClass = "B",
}) {
  const [cls, setCls] = useState(defaultClass);
  const cfg = CLASSES.find((c) => c.key === cls) || CLASSES[1];

  const yieldPct = useMemo(() => {
    const p = Number(purchasePrice);
    const r = Number(monthlyRent);
    if (!p || !r) return null;
    return r / p;   // rent-to-price ratio
  }, [purchasePrice, monthlyRent]);

  const status = useMemo(() => {
    if (yieldPct == null) return "unknown";
    if (yieldPct >= cfg.max) return "green";
    if (yieldPct >= cfg.min) return "amber";
    return "red";
  }, [yieldPct, cfg]);

  const light = {
    green:   { bg: "#D1FAE5", fg: "#065F46", dot: "#10B981", label: "On target",   Icon: Check },
    amber:   { bg: "#FEF3C7", fg: "#B45309", dot: "#F59E0B", label: "In-band",     Icon: TrendingUp },
    red:     { bg: "#FFE4E6", fg: "#9F1239", dot: "#F43F5E", label: "Below floor", Icon: AlertTriangle },
    unknown: { bg: "#F1F5F9", fg: "#475569", dot: "#94A3B8", label: "Enter rent + price", Icon: TrendingUp },
  }[status];

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-4 mb-4"
      data-testid="rent-yield-guardrail"
    >
      <div className="flex flex-wrap items-start gap-4">
        {/* Class selector */}
        <div className="flex-1 min-w-[220px]">
          <div className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">
            Market Class
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {CLASSES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCls(c.key)}
                className={`rounded-lg border px-2 py-1.5 text-left ${
                  cls === c.key
                    ? "border-indigo-500 bg-indigo-50 text-indigo-800"
                    : "border-slate-200 hover:border-slate-300 text-slate-700"
                }`}
                data-testid={`ryg-class-${c.key}`}
              >
                <div className="text-[13px] font-black">{c.key}</div>
                <div className="text-[10.5px] leading-tight opacity-70">
                  {(c.min * 100).toFixed(1)}–{(c.max * 100).toFixed(2)}%
                </div>
              </button>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">
            {cfg.label}
          </div>
        </div>

        {/* Verdict tile */}
        <div
          className="rounded-xl px-4 py-3 flex items-center gap-3 min-w-[240px]"
          style={{ backgroundColor: light.bg, color: light.fg }}
          data-testid="ryg-verdict"
        >
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: light.dot }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">
              Yield Verdict · Class {cls}
            </div>
            <div className="text-[15px] font-black tabular-nums flex items-center gap-1.5">
              <light.Icon className="w-3.5 h-3.5" />
              {light.label}
              {yieldPct != null && (
                <span className="ml-1">· {(yieldPct * 100).toFixed(2)}%</span>
              )}
            </div>
            <div className="text-[10.5px] opacity-75 mt-0.5" data-testid="ryg-target-line">
              Target: {(cfg.min * 100).toFixed(1)}–{(cfg.max * 100).toFixed(2)}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
